import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../../services/api'
import './Quiz.css'
import { MainLayout } from '../../Layout/MainLayout';

interface Alternative { id: string; text: string }
interface Question { id: string; statement: string; weight: number; alternatives: Alternative[] }
interface AttemptData { attemptId: string; timeLimitMin: number | null; questions: Question[] }
interface AlreadyPassed {
    note: number
    approved: boolean
    completedIn: string
}

export function Quiz() {
    const { quizId } = useParams()
    const navigate = useNavigate()
    const [attempt, setAttempt] = useState<AttemptData | null>(null)
    const [currentIdx, setCurrentIdx] = useState(0)
    const [answers, setAnswers] = useState<Record<string, string>>({})
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')
    const [alreadyPassed, setAlreadyPassed] = useState<AlreadyPassed | null>(null)

    useEffect(() => {
        if (!quizId) return
        api.post('/attempts/', { quizId })
            .then(r => setAttempt(r.data))
            .catch(e => {
                const data = e.response?.data

                // Já foi aprovado anteriormente
                if (data?.note === 10) {
                    setAlreadyPassed(data)
                    return
                }

                // Tentativa em andamento, retoma ela
                if (data?.attemptId && data?.questions) {
                    setAttempt(data)
                    return
                }

                setError(data?.message || 'Erro ao iniciar quiz')
            })
            .finally(() => setLoading(false))
    }, [quizId])

    function selectAnswer(questionId: string, alternativeId: string) {
        setAnswers(prev => ({ ...prev, [questionId]: alternativeId }))
    }

    async function handleFinish() {
        if (!attempt) return
        setSubmitting(true)
        try {
            const answersArray = attempt.questions.map(q => ({
                questionId: q.id,
                alternativeId: answers[q.id] || null,
            }))
            const res = await api.post(`/attempts/${attempt.attemptId}/finish`, { answers: answersArray })
            navigate(`/quiz/${quizId}/resultado`, { state: res.data })
        } catch (e: any) {
            setError(e.response?.data?.message || 'Erro ao finalizar quiz')
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) return <div className="container fade-in" style={{ paddingTop: 60, color: 'var(--text-3)', textAlign: 'center' }}>Iniciando quiz...</div>
    if (error) return <div className="container fade-in" style={{ paddingTop: 60 }}><div style={{ color: 'var(--red)', textAlign: 'center' }}>{error}</div></div>
    if (alreadyPassed) {
        return (
            <div className="container fade-in">
                <div className="result-wrap">
                    <div className="result-medal">
                        <div className="result-medal-inner">✦</div>
                    </div>
                    <div className="result-kicker">Já concluído</div>
                    <h1 className="result-title serif">Você já foi aprovado!</h1>
                    <p className="result-sub">
                        Concluído em {new Date(alreadyPassed.completedIn).toLocaleDateString('pt-BR')}
                    </p>

                    <div className="result-score">
                        <span className="big">{Number(alreadyPassed.note).toFixed(1)}</span>
                        <span className="small">/ 10</span>
                    </div>
                    <br></br>
                    <button className="btn btn-primary" onClick={() => navigate('/catalogo')}>
                        ← Voltar ao catálogo
                    </button>
                </div>
            </div>
        )
    }
    if (!attempt) return null

    const questions = attempt.questions
    const current = questions[currentIdx]
    const isLast = currentIdx === questions.length - 1
    const answered = answers[current?.id]
    const totalAnswered = Object.keys(answers).length

    const letters = ['A', 'B', 'C', 'D', 'E']

    return (
        <MainLayout>
            <div className="container fade-in">
                <div className="quiz-wrap">
                    <div className="quiz-progress">
                        <span>{currentIdx + 1} / {questions.length}</span>
                        <div className="quiz-dots">
                            {questions.map((q, i) => (
                                <div
                                    key={q.id}
                                    className={`quiz-dot ${i < currentIdx ? 'done' : i === currentIdx ? 'current' : ''}`}
                                />
                            ))}
                        </div>
                        <span>{totalAnswered} respondidas</span>
                    </div>

                    <div className="quiz-card">
                        <div className="quiz-q-num">Questão {currentIdx + 1}</div>
                        <div className="quiz-q-text serif">{current.statement}</div>
                        <div className="quiz-options">
                            {current.alternatives.map((alt, i) => (
                                <button
                                    key={alt.id}
                                    className={`quiz-option ${answers[current.id] === alt.id ? 'selected' : ''}`}
                                    onClick={() => selectAnswer(current.id, alt.id)}
                                >
                                    <span className="letter">{letters[i]}</span>
                                    {alt.text}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="quiz-actions">
                        <button
                            className="btn btn-ghost"
                            onClick={() => setCurrentIdx(i => i - 1)}
                            disabled={currentIdx === 0}
                        >
                            ← Anterior
                        </button>

                        {isLast ? (
                            <button
                                className="btn btn-primary"
                                onClick={handleFinish}
                                disabled={submitting}
                            >
                                {submitting ? 'Enviando...' : 'Finalizar quiz →'}
                            </button>
                        ) : (
                            <button
                                className="btn btn-primary"
                                onClick={() => setCurrentIdx(i => i + 1)}
                            >
                                Próxima →
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    )
}